/**
 * 主体（角色 / 场景 / 道具）资产的数据契约（P2-主体）。
 *
 * 主体是「带一致性锚点的图像资产」：复用图像生成能力出参考图/三视图，锁定后作为下游
 * 分镜/视频生成的标准参考（对齐调研 §三 主体行）。这里只放类型 + 工厂 + 纯函数，
 * 生成/回填由 generationService + 编辑器负责。
 */
import type { CanvasNode, GenerationParams, Vec2 } from './canvas';
import { isLiteraryScriptResult, isStoryboardResult, type GeneratedPayload } from './generation';
import { styleSuffix } from './styles';
import type { VoiceProfile } from './voice';

export type SubjectCategory = 'character' | 'scene' | 'prop';

export const SUBJECT_CATEGORY_LABELS: Record<SubjectCategory, string> = {
  character: '角色',
  scene: '场景',
  prop: '道具',
};

/** 新建卡时自动生成的首个造型名。 */
export const DEFAULT_LOOK_NAME = '默认造型';

/** 一张资产卡内的一类定妆（默认造型 + 各换装/状态），内含若干视图节点引用（正/侧/背 + 细节）。 */
export interface SubjectLook {
  id: string;
  name: string;
  /** 归属本造型的视图节点 id（由 spawnViews 派生后挂上，非破坏追加） */
  viewNodeIds: string[];
}

/** 主体节点 payload（存进 CanvasNode.payload） */
export interface SubjectPayload {
  type: 'subject';
  category: SubjectCategory;
  name: string;
  description: string;
  /** 一致性锚点：锁定后作为下游生成的标准参考 */
  locked: boolean;
  /** 多造型（每卡多造型/多视图，调研 §十）；老数据可能缺失，读取前用 ensureLooks 归一 */
  looks: SubjectLook[];
  /** 当前正在编辑/落图的造型 id */
  activeLookId: string;
  /** 绑定音色（A 决策：一个角色一条声线，音频卡继承）；未设则用默认 */
  voice?: VoiceProfile | undefined;
  /** 质感风格预设 id（P-C1）；空/未设=不附加风格 */
  style?: string | undefined;
  /** 角色小传（P-C3）：补背景、也可驱动 AI 造声（仅角色用） */
  bio?: string | undefined;
}

export function isSubjectPayload(p: unknown): p is SubjectPayload {
  return (
    typeof p === 'object' &&
    p !== null &&
    (p as { type?: unknown }).type === 'subject' &&
    typeof (p as { category?: unknown }).category === 'string'
  );
}

/** 造一个主体节点：kind subject、mode generator、携 payload + 默认 params（与图像卡共用生成控件）。 */
export function createSubjectNode(category: SubjectCategory, id: string, position: Vec2): CanvasNode {
  const label = SUBJECT_CATEGORY_LABELS[category];
  const lookId = `look-${id}-0`;
  const payload: SubjectPayload = {
    type: 'subject',
    category,
    name: label,
    description: '',
    locked: false,
    looks: [{ id: lookId, name: DEFAULT_LOOK_NAME, viewNodeIds: [] }],
    activeLookId: lookId,
  };
  const params: GenerationParams = {
    model: 'wan2.7-image',
    capability: 'textToImage',
    aspectRatio: '3:4',
    resolution: '2K',
    durationSec: 0,
    count: 1,
    prompt: '',
    negativePrompt: '',
    enhancePrompt: false,
  };
  return { id, kind: 'subject', mode: 'generator', title: label, position, payload, params };
}

/** 主体生成用的正向提示词：按类别强化（规范 §3.3–3.5）+ 质感风格后缀。 */
export function subjectPrompt(p: SubjectPayload): string {
  const style = styleSuffix(p.style);
  const tail = style === '' ? '' : `，${style}`;
  const base = `${p.name}，${p.description}`.replace(/^，|，$/g, '').trim();
  if (p.category === 'scene') {
    return `${base}，无人空镜，影视场景设定，宽银幕构图，专业灯光，高清细节${tail}`;
  }
  if (p.category === 'prop') {
    return `道具设计图，${base}，纯色背景，产品摄影，高清细节，多角度${tail}`;
  }
  // 角色：名称 + 统一视觉档案（供三视图/各造型生成保持同一人）
  return `${base}${tail}`;
}

/** 视图组提示词（P-C2 按类别）：角色=正/侧/背/脸部特写；场景=多视角(含反打)；道具=多角度。 */
export function turnaroundPrompts(p: SubjectPayload): { label: string; prompt: string }[] {
  const base = subjectPrompt(p);
  if (p.category === 'scene') {
    return [
      { label: '主视角', prompt: `${base}，主视角全景` },
      { label: '左移', prompt: `${base}，向左平移视角` },
      { label: '右移', prompt: `${base}，向右平移视角` },
      { label: '俯视', prompt: `${base}，俯视角度` },
      { label: '仰视', prompt: `${base}，仰视角度` },
      { label: '反打', prompt: `${base}，180度反打视角` },
    ];
  }
  if (p.category === 'prop') {
    return [
      { label: '主视图', prompt: `${base}，主视图` },
      { label: '细节', prompt: `${base}，细节特写` },
      { label: '斜侧', prompt: `${base}，斜45度视角` },
    ];
  }
  // 角色：三视图 + 脸部特写
  return [
    { label: '正面', prompt: `${base}，正面视图，设定图，中性均匀光照，纯色背景` },
    { label: '侧面', prompt: `${base}，侧面视图，设定图，中性均匀光照，纯色背景` },
    { label: '背面', prompt: `${base}，背面视图，设定图，中性均匀光照，纯色背景` },
    { label: '脸部特写', prompt: `${base}，脸部特写，五官清晰，设定图，纯色背景` },
  ];
}

/* ---------- 多造型 / 多视图（刀 4a，调研 §十）：均为不可变纯函数 ----------
 * 均先经 ensureLooks 归一（兼容无 looks 的老卡），返回新 payload；不原地修改入参。 */

/** 归一：无造型时补一个默认造型；activeLookId 失效时回退到首个。 */
export function ensureLooks(p: SubjectPayload): SubjectPayload {
  const looks = Array.isArray(p.looks) ? p.looks : [];
  if (looks.length > 0) {
    const activeLookId = looks.some((l) => l.id === p.activeLookId) ? p.activeLookId : (looks[0]?.id ?? '');
    return { ...p, looks, activeLookId };
  }
  const id = 'look-default';
  return { ...p, looks: [{ id, name: DEFAULT_LOOK_NAME, viewNodeIds: [] }], activeLookId: id };
}

/** 当前正在编辑/落图的造型。 */
export function activeLook(p: SubjectPayload): SubjectLook | undefined {
  const base = ensureLooks(p);
  return base.looks.find((l) => l.id === base.activeLookId) ?? base.looks[0];
}

/** 新增一个造型并将其设为当前（id 由调用方给定以保证唯一）。 */
export function addLook(p: SubjectPayload, opts: { id: string; name?: string }): SubjectPayload {
  const base = ensureLooks(p);
  const clean = opts.name?.trim() ?? '';
  const look: SubjectLook = {
    id: opts.id,
    name: clean !== '' ? clean : `造型${base.looks.length + 1}`,
    viewNodeIds: [],
  };
  return { ...base, looks: [...base.looks, look], activeLookId: look.id };
}

/** 重命名造型；空名忽略（保持原名）。 */
export function renameLook(p: SubjectPayload, lookId: string, name: string): SubjectPayload {
  const base = ensureLooks(p);
  const clean = name.trim();
  if (clean === '') {
    return base;
  }
  return { ...base, looks: base.looks.map((l) => (l.id === lookId ? { ...l, name: clean } : l)) };
}

/** 删除造型；至少保留一个（否则原样返回）；删掉当前造型时自动切到首个。 */
export function removeLook(p: SubjectPayload, lookId: string): SubjectPayload {
  const base = ensureLooks(p);
  if (base.looks.length <= 1) {
    return base;
  }
  const looks = base.looks.filter((l) => l.id !== lookId);
  const activeLookId = base.activeLookId === lookId ? (looks[0]?.id ?? '') : base.activeLookId;
  return { ...base, looks, activeLookId };
}

/** 切换当前造型；目标不存在则原样返回。 */
export function setActiveLook(p: SubjectPayload, lookId: string): SubjectPayload {
  const base = ensureLooks(p);
  if (!base.looks.some((l) => l.id === lookId)) {
    return base;
  }
  return { ...base, activeLookId: lookId };
}

/** 把一批视图节点 id 追加到指定造型（去重，非破坏）。 */
export function attachViewsToLook(p: SubjectPayload, lookId: string, viewNodeIds: string[]): SubjectPayload {
  const base = ensureLooks(p);
  return {
    ...base,
    looks: base.looks.map((l) => {
      if (l.id !== lookId) {
        return l;
      }
      const merged = [...l.viewNodeIds];
      for (const v of viewNodeIds) {
        if (!merged.includes(v)) {
          merged.push(v);
        }
      }
      return { ...l, viewNodeIds: merged };
    }),
  };
}

/** 从剧本抽取出的一个实体种子（→ 一张资产卡的预填内容）。 */
export interface EntitySeed {
  category: SubjectCategory;
  name: string;
  description: string;
  /** 角色专属：多套造型名（方案①——造型挂角色卡下）；暂无描写则空，卡自带默认造型 */
  looks: string[];
}

/**
 * 从上游产物（分镜表/文学剧本）抽取实体清单：角色 + 场景，按名去重，
 * 描述从相关动作/对白聚合。供“从剧本一键生成资产卡”预填名称/描述。纯函数可测。
 */
export function extractEntities(result: GeneratedPayload | undefined): EntitySeed[] {
  const chars = new Map<string, string[]>();
  const scenes = new Map<string, string[]>();
  const props = new Map<string, string[]>();
  const add = (map: Map<string, string[]>, key: string, note: string | undefined) => {
    if (key === '') {
      return;
    }
    const arr = map.get(key) ?? [];
    if (note !== undefined && note !== '' && !arr.includes(note)) {
      arr.push(note);
    }
    map.set(key, arr);
  };
  if (isStoryboardResult(result)) {
    for (const shot of result.storyboard.shots) {
      add(scenes, shot.scene, shot.action);
      for (const c of shot.characters) {
        add(chars, c, shot.action);
      }
      for (const pr of shot.props) {
        add(props, pr, shot.action);
      }
    }
  } else if (isLiteraryScriptResult(result)) {
    for (const ep of result.script.episodes) {
      for (const sc of ep.scenes) {
        add(scenes, sc.heading, sc.action);
        for (const c of sc.characters) {
          add(chars, c, sc.action);
        }
        for (const dl of sc.dialogues) {
          add(chars, dl.character, dl.line);
        }
      }
    }
  }
  const out: EntitySeed[] = [];
  for (const [name, notes] of chars) {
    out.push({ category: 'character', name, description: notes.slice(0, 2).join('；'), looks: [] });
  }
  for (const [name, notes] of scenes) {
    out.push({ category: 'scene', name, description: notes.slice(0, 2).join('；'), looks: [] });
  }
  for (const [name, notes] of props) {
    out.push({ category: 'prop', name, description: notes.slice(0, 2).join('；'), looks: [] });
  }
  return out;
}
